import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/common/helpers/utils";

export enum TooltipVariant {
  Primary = "primary",
  Secondary = "secondary",
  Success = "success",
  Warning = "warning",
  Error = "error",
  Outlined = "outlined",
  OutlinedPrimary = "outlined-primary",
  OutlinedSecondary = "outlined-secondary",
  OutlinedError = "outlined-error",
  OutlinedWarning = "outlined-warning",
  OutlinedSuccess = "outlined-success",
  Ghost = "ghost",
}

const TooltipProviderContext = React.createContext(false);

function TooltipProvider({ children, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipProviderContext value={true}>
      <TooltipPrimitive.Provider {...props}>{children}</TooltipPrimitive.Provider>
    </TooltipProviderContext>
  );
}

function TooltipProviderIfMissing({ children }: React.PropsWithChildren) {
  const hasTooltipProvider = React.use(TooltipProviderContext);

  return hasTooltipProvider ? children : <TooltipProvider>{children}</TooltipProvider>;
}

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TOOLTIP_VARIANT_CLASSES = {
  [TooltipVariant.Primary]: "border-transparent bg-primary text-primary-foreground",
  [TooltipVariant.Secondary]: "border-transparent bg-secondary text-secondary-foreground",
  [TooltipVariant.Success]: "border-transparent bg-success text-success-foreground",
  [TooltipVariant.Warning]: "border-transparent bg-warning text-warning-foreground",
  [TooltipVariant.Error]: "border-transparent bg-error text-error-foreground",
  [TooltipVariant.Outlined]: "border border-muted-foreground/30 bg-background text-muted-foreground",
  [TooltipVariant.OutlinedPrimary]: "border border-primary/50 bg-background text-primary",
  [TooltipVariant.OutlinedSecondary]: "border border-secondary/50 bg-background text-secondary",
  [TooltipVariant.OutlinedError]: "border border-error/50 bg-background text-error",
  [TooltipVariant.OutlinedWarning]: "border border-warning/50 bg-background text-warning",
  [TooltipVariant.OutlinedSuccess]: "border border-success/50 bg-background text-success",
  [TooltipVariant.Ghost]: "border border-transparent bg-transparent text-muted-foreground",
} satisfies Record<TooltipVariant, string>;

const tooltipVariants = cva(
  cn(
    "animate-in fade-in-0 z-50 overflow-hidden rounded-md px-3 py-1.5 text-xs",
    "zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
    "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2",
    "data-[side=top]:slide-in-from-bottom-2",
  ),
  {
    variants: {
      variant: TOOLTIP_VARIANT_CLASSES,
    },
    defaultVariants: {
      variant: TooltipVariant.Outlined,
    },
  },
);

const TooltipContent = ({
  className,
  sideOffset = 4,
  variant,
  ref,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & VariantProps<typeof tooltipVariants>) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(tooltipVariants({ variant }), className)}
      {...props}
    />
  </TooltipPrimitive.Portal>
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipContent, TooltipProvider, TooltipProviderIfMissing, TooltipTrigger };
